import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { F, T, Over, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Section, List, Row, Empty, Loading, TextLink } from "@/components/calm/Calm";
import RoomView, { fmtDate, fmtInstant, addressLine } from "@/components/deal/RoomView";

// Deal room, agent side (DEAL_ROOM). Who is on the other side of the deal, what each one
// sees, and one private link per person. Bricks never sends the link: the agent copies it
// or opens it in their own mail app.

function linkLine(link) {
  if (!link || link.status === "none") return "No link yet";
  if (link.status === "revoked") return "Link turned off";
  if (link.status === "expired") return `Link expired ${fmtInstant(link.expires_at)}`;
  const views = link.view_count ? `opened ${link.view_count} ${link.view_count === 1 ? "time" : "times"}` : "not opened yet";
  return `Link on until ${fmtInstant(link.expires_at)}, ${views}`;
}

function mailto(party, url, address) {
  const subject = `${address}: your deal page`;
  const body = `Hi ${party.name.split(" ")[0]},\n\nHere is your page for ${address}. It shows the dates, conditions and documents that matter for your part of the deal, and it stays up to date.\n\n${url}\n\nThe link is just for you, so please don't forward it.\n`;
  return `mailto:${encodeURIComponent(party.email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const BLANK = { role: "buyer_lawyer", name: "", company: "", email: "", phone: "" };

function presetFor(role, roles, catalogue) {
  const r = (roles || []).find((x) => x.key === role);
  const pre = r?.preset || { dates: [], price: false, conditions: [], doc_types: [], hand_back: false };
  const types = new Set(pre.doc_types || []);
  return {
    dates: [...(pre.dates || [])], price: !!pre.price, conditions: [...(pre.conditions || [])],
    documents: (catalogue?.documents || []).filter((d) => types.has(d.doc_type)).map((d) => d.id),
    hand_back: !!pre.hand_back,
  };
}

function Check({ checked, onChange, label, hint, p, testid }) {
  return (
    <label data-testid={testid} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", cursor: "pointer", minWidth: 0 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, margin: "1px 0 0", accentColor: p.ink, flexShrink: 0 }} />
      <span style={{ minWidth: 0, fontSize: 14, color: p.ink, overflowWrap: "anywhere" }}>
        {label}{hint ? <span style={{ display: "block", fontSize: 12.5, color: p.ink3, marginTop: 2 }}>{hint}</span> : null}
      </span>
    </label>
  );
}

function Group({ title, children, p }) {
  return (
    <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: p.ink2, marginBottom: 2 }}>{title}</div>
      {children}
    </div>
  );
}

export default function DealRoomTab({ dealId, base, getAuthHeaders: authProp, dark, p, inputStyle, onOpenTab }) {
  const authRef = useRef(authProp);
  authRef.current = authProp;
  const getAuthHeaders = useCallback(() => authRef.current(), []);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | party
  const [form, setForm] = useState(BLANK);
  const [scope, setScope] = useState({ dates: [], price: false, conditions: [], documents: [], hand_back: false });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [preview, setPreview] = useState(null); // { party, view }
  const [sharing, setSharing] = useState(null); // { party, url, events }
  const [reminder, setReminder] = useState(null); // null | "loading" | { mode, on, has_email, email }

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room`, { headers: await getAuthHeaders() });
      if (!r.ok) throw new Error();
      setRoom(await r.json());
      setError("");
    } catch {
      setError("The deal room didn't load. Try again in a moment.");
    }
  }, [base, dealId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const cat = room?.catalogue;
  const kinds = useMemo(() => {
    const all = cat?.conditions || [];
    return [...all.filter((k) => k.on_deal), ...all.filter((k) => !k.on_deal)];
  }, [cat]);

  const openNew = () => {
    setForm(BLANK); setScope(presetFor(BLANK.role, room?.roles, cat)); setFormError(""); setEditing("new");
  };
  const openEdit = (party) => {
    setForm({ role: party.role, name: party.name || "", company: party.company || "", email: party.email || "", phone: party.phone || "" });
    setScope({ ...party.scope }); setFormError(""); setReminder(null); setEditing(party);
  };
  const changeRole = (role) => {
    setForm((f) => ({ ...f, role }));
    if (editing === "new") setScope(presetFor(role, room?.roles, cat));
  };
  const toggle = (key, value, on) => setScope((s) => ({ ...s, [key]: on ? [...s[key].filter((v) => v !== value), value] : s[key].filter((v) => v !== value) }));

  const save = async () => {
    if (!form.name.trim()) { setFormError("Add their name."); return; }
    setBusy(true); setFormError("");
    const isNew = editing === "new";
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties${isNew ? "" : `/${editing.id}`}`, {
        method: isNew ? "POST" : "PATCH", headers: await getAuthHeaders(), body: JSON.stringify({ ...form, scope }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setFormError(d?.detail?.message || (typeof d?.detail === "string" ? d.detail : "That didn't save. Try again."));
        return;
      }
      toast.success(isNew ? `${d.name} added` : "Saved");
      setEditing(null);
      load();
    } catch {
      setFormError("That didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing || editing === "new") return;
    if (!window.confirm(`Remove ${editing.name} from this deal? They'll lose anything you shared with them.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${editing.id}`, { method: "DELETE", headers: await getAuthHeaders() });
      if (!r.ok) throw new Error();
      toast.success(`${editing.name} removed`);
      setEditing(null);
      load();
    } catch {
      setFormError("That didn't remove. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async (party) => {
    setPreview({ party, view: null });
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${party.id}/preview`, { headers: await getAuthHeaders() });
      if (!r.ok) throw new Error();
      setPreview({ party, view: await r.json() });
    } catch {
      setPreview(null);
      toast.error("The preview didn't load. Try again.");
    }
  };

  const previewReminder = async () => {
    setReminder("loading");
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${editing.id}/reminder`, { headers: await getAuthHeaders() });
      if (!r.ok) throw new Error();
      setReminder(await r.json());
    } catch {
      setReminder(null);
      toast.error("The reminder preview didn't load. Try again.");
    }
  };

  const openSharing = async (party) => {
    setSharing({ party, url: "", events: null });
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${party.id}/activity`, { headers: await getAuthHeaders() });
      const d = r.ok ? await r.json() : { events: [] };
      setSharing((s) => (s && s.party.id === party.id ? { ...s, events: d.events || [] } : s));
    } catch {
      setSharing((s) => (s ? { ...s, events: [] } : s));
    }
  };

  const makeLink = async () => {
    const party = sharing.party;
    if (party.link?.status === "active" && !window.confirm(`Make a new link for ${party.name}? The link you sent before will stop working.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${party.id}/link`, { method: "POST", headers: await getAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error();
      setSharing((s) => ({ ...s, url: d.url, party: { ...s.party, link: d.link } }));
      load();
    } catch {
      toast.error("The link wasn't made. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    const party = sharing.party;
    if (!window.confirm(`Turn off ${party.name}'s link? It stops working straight away.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/api/transactions/${dealId}/room/parties/${party.id}/link`, { method: "DELETE", headers: await getAuthHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error();
      setSharing((s) => ({ ...s, url: "", party: { ...s.party, link: d.link } }));
      toast.success("Link turned off");
      load();
    } catch {
      toast.error("The link is still on. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sharing.url);
      toast.success("Link copied");
    } catch {
      const el = document.querySelector('[data-testid="room-link-url"]');
      if (el) { el.focus(); el.select(); }
      toast("Select the link and copy it");
    }
  };

  if (error) return <Empty testid="room-error" action={<TextLink hit onClick={load}>Try again</TextLink>}>{error}</Empty>;
  if (!room) return <Loading />;
  const parties = room.parties || [];
  const field = { ...inputStyle, background: p.bg };
  const dialogStyle = { maxWidth: 520, width: "calc(100vw - 24px)", maxHeight: "calc(100dvh - 32px)", overflowY: "auto", borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: "var(--bx-pad)" };
  const v = preview?.view;

  return (
    <div data-testid="deal-room" style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: p.ink3, maxWidth: 560, flex: "1 1 240px" }}>
          Lawyers, lenders, inspectors and stagers on this deal. Pick what each person can see, then give them their own link. Nothing is shared until you do.
        </div>
        {parties.length > 0 && <Btn variant="primary" dark={dark} icon="plus" onClick={openNew} data-testid="room-add">Add person</Btn>}
      </div>

      {(room.waiting || []).length > 0 && (
        <Section title="Waiting on you" testid="room-waiting">
          <List>
            {room.waiting.map((w) => (
              <Row key={`${w.kind}-${w.id}-${w.party_id}`} testid={`room-waiting-${w.kind}`}
                title={w.kind === "document" ? w.what : `${w.what} is met`}
                meta={[w.kind === "document" ? `Sent by ${w.party}` : `${w.party} says`, fmtInstant(w.at, true)].filter(Boolean)}
                next={w.note ? `“${w.note}”` : (w.kind === "document" ? "Accept it or send it back in Documents." : "Record the outcome in Conditions when you're ready.")}
                aside={onOpenTab ? <TextLink hit onClick={() => onOpenTab(w.kind === "document" ? "Documents" : "Conditions")} testid="room-waiting-open">
                  {w.kind === "document" ? "Review" : "Open conditions"}</TextLink> : null} />
            ))}
          </List>
        </Section>
      )}

      <Section title="People on this deal" testid="room-parties">
        {parties.length === 0 ? (
          <Empty testid="room-empty" action={<Btn variant="primary" dark={dark} icon="plus" onClick={openNew} data-testid="room-add">Add person</Btn>}>
            No one added yet. Start with the lawyers and the mortgage broker.
          </Empty>
        ) : (
          <List>
            {parties.map((party) => (
              <Row key={party.id} testid={`room-party-${party.id}`}
                title={party.name}
                meta={[party.role_label, party.company, party.email].filter(Boolean)}
                next={[party.sees === "Nothing shared yet" ? party.sees : `Sees ${party.sees}`, party.scope?.hand_back ? "can send things back" : null, party.reminders_on ? "gets reminders" : null, linkLine(party.link)].filter(Boolean)}
                aside={<span style={{ display: "inline-flex", gap: 14 }}>
                  <TextLink hit onClick={() => openSharing(party)} testid="room-share">Share</TextLink>
                  <TextLink hit onClick={() => openPreview(party)} testid="room-preview">Preview</TextLink>
                  <TextLink hit onClick={() => openEdit(party)} testid="room-edit">Edit</TextLink>
                </span>} />
            ))}
          </List>
        )}
      </Section>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent style={dialogStyle} data-testid="room-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>{editing === "new" ? "Add person" : `Edit ${editing?.name || ""}`}</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Their role sets a starting point for what they see. Change any of it.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 20, minWidth: 0 }}>
            <div><Over color={p.ink3}>Role</Over>
              <select value={form.role} onChange={(e) => changeRole(e.target.value)} style={field} data-testid="room-role">
                {(room.roles || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>Name</Over>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Natarajan" style={inputStyle} data-testid="room-name" />
            </div>
            <div><Over color={p.ink3}>Firm (optional)</Over>
              <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="e.g. Natarajan Law" style={inputStyle} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
              <div><Over color={p.ink3}>Email (optional)</Over>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} data-testid="room-email" />
              </div>
              <div><Over color={p.ink3}>Phone (optional)</Over>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
              </div>
            </div>

            <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, marginTop: 8 }}>What they see</div>
            <Group title="Key dates" p={p}>
              {(cat?.dates || []).map((d) => (
                <Check key={d.key} p={p} testid={`room-date-${d.key}`} checked={scope.dates.includes(d.key)} onChange={(on) => toggle("dates", d.key, on)}
                  label={d.label} hint={fmtDate(d.date)} />
              ))}
              <Check p={p} testid="room-price" checked={!!scope.price} onChange={(on) => setScope((s) => ({ ...s, price: on }))} label="Sale price and deposit" />
            </Group>
            <Group title="Conditions" p={p}>
              {kinds.map((k) => (
                <Check key={k.key} p={p} testid={`room-cond-${k.key}`} checked={scope.conditions.includes(k.key)} onChange={(on) => toggle("conditions", k.key, on)}
                  label={k.label} hint={k.on_deal ? null : "Not on this deal yet"} />
              ))}
            </Group>
            <Group title="Documents" p={p}>
              {(cat?.documents || []).length === 0 && <div style={{ fontSize: 13, color: p.ink3, padding: "8px 0" }}>No documents on this deal yet.</div>}
              {(cat?.documents || []).map((d) => (
                <Check key={d.id} p={p} testid={`room-doc-${d.id}`} checked={scope.documents.includes(d.id)} onChange={(on) => toggle("documents", d.id, on)}
                  label={d.name} hint={d.doc_type} />
              ))}
            </Group>
            <Group title="Sending back" p={p}>
              <Check p={p} testid="room-hand-back" checked={!!scope.hand_back} onChange={(on) => setScope((s) => ({ ...s, hand_back: on }))}
                label="Can send things back" hint="They can send you documents and tell you a condition is met. You review each one; nothing on the deal changes until you do." />
            </Group>
            {editing && editing !== "new" ? (
              <Group title="Reminders" p={p}>
                <div data-testid="room-reminder-state" style={{ fontSize: 13, lineHeight: 1.55, color: p.ink2, padding: "8px 0" }}>
                  {editing.reminders_on
                    ? `${editing.name.split(" ")[0]} turned on reminders. They get an email two days before each deadline they can see, including the deposit and closing if you share them. The email has no link to their page.`
                    : `${editing.name.split(" ")[0]} can turn on email reminders from their page${editing.email ? "" : " once you add their email"}. Each one goes out two days before a date or deadline they can see.`}
                </div>
                <div><Btn variant="ghost" dark={dark} onClick={previewReminder} disabled={reminder === "loading"} data-testid="room-reminder-preview">
                  {reminder === "loading" ? "Loading…" : "Preview reminder"}
                </Btn></div>
                {reminder && reminder !== "loading" ? (
                  <div data-testid="room-reminder-email" style={{ border: `1px solid ${p.hair}`, borderRadius: 2, padding: 14, marginTop: 10, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                    {reminder.email ? (
                      <>
                        <div style={{ fontSize: 12.5, color: p.ink3, overflowWrap: "anywhere" }}>To {reminder.email.to} · replies come to you</div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: p.ink, overflowWrap: "anywhere" }}>{reminder.email.subject}</div>
                        <div style={{ fontSize: 13, lineHeight: 1.6, color: p.ink2, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{reminder.email.text}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: p.ink3 }}>
                        {reminder.has_email ? "Nothing is coming up in what they can see, so there's no reminder to show yet." : "Add their email to preview a reminder."}
                      </div>
                    )}
                  </div>
                ) : null}
              </Group>
            ) : null}
            <div style={{ fontSize: 12.5, color: p.ink3 }}>They never see commission, your notes, your client's contact details or anyone else on the deal.</div>
            {formError && <div role="alert" style={{ fontSize: 13, color: T.brickDeep }}>{formError}</div>}
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10, flexWrap: "wrap" }}>
            {editing && editing !== "new" && <Btn variant="ghost" dark={dark} onClick={remove} disabled={busy} style={{ marginRight: "auto" }} data-testid="room-remove">Remove</Btn>}
            <Btn variant="ghost" dark={dark} onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={save} disabled={busy} data-testid="room-save">{editing === "new" ? "Add person" : "Save"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent style={dialogStyle} data-testid="room-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>What {preview?.party?.name} sees</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Exactly their view, nothing more.
            </DialogDescription>
          </DialogHeader>
          {!v ? <Loading /> : <RoomView v={v} p={p} />}
          <DialogFooter style={{ marginTop: 24 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setPreview(null)}>Close</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!sharing} onOpenChange={(o) => { if (!o) setSharing(null); }}>
        <DialogContent style={dialogStyle} data-testid="room-share-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>{sharing?.party?.name}'s link</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              A private page with only what you picked for them. No account needed. Bricks doesn't send it; you do.
            </DialogDescription>
          </DialogHeader>
          {sharing && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 18, minWidth: 0 }}>
              <div data-testid="room-link-status" style={{ fontSize: 14, color: p.ink }}>{linkLine(sharing.party.link)}</div>
              {sharing.url ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                  <input readOnly value={sharing.url} data-testid="room-link-url" onFocus={(e) => e.target.select()} aria-label="Link"
                    style={{ ...field, fontSize: 13, fontFamily: F.mono || "inherit" }} />
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Btn variant="primary" dark={dark} onClick={copy} data-testid="room-link-copy">Copy link</Btn>
                    {sharing.party.email ? (
                      <Btn as="a" variant="ghost" dark={dark} href={mailto(sharing.party, sharing.url, addressLine(room.property) || "Your deal")} data-testid="room-link-mail">Open in my email</Btn>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 12.5, color: p.ink3 }}>This is the only time the full link is shown. If you lose it, make a new one.</div>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {!sharing.url && (
                  <Btn variant="primary" dark={dark} onClick={makeLink} disabled={busy} data-testid="room-link-make">
                    {sharing.party.link?.status === "active" ? "Make a new link" : "Make link"}
                  </Btn>
                )}
                {sharing.party.link?.status === "active" && (
                  <Btn variant="ghost" dark={dark} onClick={turnOff} disabled={busy} data-testid="room-link-off">Turn off link</Btn>
                )}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: p.ink, marginBottom: 6 }}>Activity</div>
                <div style={{ borderTop: `1px solid ${p.hair}` }} data-testid="room-activity">
                  {sharing.events === null ? <div style={{ fontSize: 13, color: p.ink3, padding: "10px 0" }}>Loading…</div>
                    : sharing.events.length === 0 ? <div style={{ fontSize: 13, color: p.ink3, padding: "10px 0" }}>Nothing yet.</div>
                    : sharing.events.slice(0, 12).map((e) => (
                      <div key={e.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: `1px solid ${p.hair}`, fontSize: 13, minWidth: 0 }}>
                        <span style={{ color: p.ink, overflowWrap: "anywhere", minWidth: 0 }}>{e.label}{e.what ? `: ${e.what}` : ""}</span>
                        <span style={{ color: p.ink3, whiteSpace: "nowrap", flexShrink: 0 }}>{fmtInstant(e.at, true)}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter style={{ marginTop: 24 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setSharing(null)}>Done</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
